<i18n>
{
	"en": {
		"found": "{count} found"
	},
	"no": {
		"found": "{count} treff"
	}
}
</i18n>
<template>
	<t-form-group :title="title" rowGap="5px">

		<template #header>
			<slot name="header" />
		</template>

		<span class="first-line">
			<t-popover
				:showPopover="shouldShowPopover"
				:marginY="6"
				:style="getStyle"
				position="bottom"
				:min-width="150"
				relative

				v-bind="$attrs"
			>
				<t-text-input-button
					slot="target"
					ref="input"
					:value="searchValue"
					:placeholder="placeholder"
					:description="description"
					:isInvalid="isInvalid"
					:validationMessage="validationMessage"
					:warningMessage="warningMessage"
					:disabled="disabled"
					:disableButtonTabbing="disableButtonTabbing"
					:buttonIsOnlyVisual="buttonIsOnlyVisual"
					:icon="icon"
					:iconType="iconType"
					relative

					@input="onInput"
					@change="onChange"
					@button:click="onButtonClick"
					@blur="onBlur"
					@focus="onFocus($event)"
					@keydown.tab="onTab"
					@keydown.enter="onEnter"
				/>

				<t-menu v-if="filteredItems" style="max-height:200px">
					<t-menu-options-group
						ref="menuOptions"
						v-slot="slotProps"
						:title="showCount ? $tc('found', filteredItems.length, { count: filterTitle }) : ''"

						:items="filteredItems"
						:selectedItems="selectedItems"
						:selectedItem="selectedItem"
						disableSpaceToSelect
						groupStyle="combobox"
						class="selectMeDamnit"

						@change="onSelected"
						@update:focusedIndex="focusedIndex = $event"
					>
						<slot
							name="optionItem"
							:item="slotProps.item"
							:isSelected="slotProps.isSelected"
						>
							<t-menu-combobox-option-item
								:value="slotProps.item"
								:selected="slotProps.isSelected"
								:focused="slotProps.isFocused"
								height="40px"
							>
								<slot
									name="optionItemContent"
									:item="slotProps.item"
								>
									{{ displayKey
										? slotProps.item[displayKey]
										: slotProps.item }}
								</slot>
							</t-menu-combobox-option-item>

						</slot>

					</t-menu-options-group>
				</t-menu>
			</t-popover>
		</span>

		<slot name="afterInput" />
	</t-form-group>
</template>

<script>
import inputWrapperMixin from '@/components/evergreen/inputs/inputWrapperMixin';
import formMixin from '@/components/evergreen/inputs/formMixin';
import popoverMixin from '@/components/evergreen/inputs/popoverMixin';
import TMenuOptionsGroup from '@/components/evergreen/menu/TMenuOptionsGroup';
import TTextInputButton from '@/components/evergreen/inputs/TTextInputButton';
import TMenuComboboxOptionItem from '@/components/evergreen/menu/TMenuComboboxOptionItem';

export default {
	name: 'TAdvancedComboButtonBox',
	components: { TMenuComboboxOptionItem, TTextInputButton, TMenuOptionsGroup },
	mixins: [ inputWrapperMixin, formMixin, popoverMixin ],
	props: {
		selectedItems: Array,
		selectedItem: Object,
		title: String,
		displayKey: String,
		items: {
			type: Array,
			default: () => []
		},
		itemFilterFunc: Function,
		icon: {
			type: String,
			default: 'geosearch'
		},
		iconType: {
			type: String,
			default: 'bp'
		},
		limit: Number,
		resetOnSelect: {
			type: Boolean,
			default: false
		},
		showPopoverOnlyOnSearch: {
			type: Boolean,
			default: false
		},
		disabled: {
			type: Boolean,
			default: false
		},
		disableButtonTabbing: {
			type: Boolean,
			default: false
		},
		buttonIsOnlyVisual: {
			type: Boolean,
			default: false
		},
		showCount: {
			type: Boolean,
			default: true
		},
		eagerLoad: Boolean,
		/**
		 * Set to true if the parent component loads all items in one go, and does not reload upon this component
		 * emitting the updateFilters event
		 */
		immediatelyUpdateFilteredItems: Boolean,
	},
	data() {
		return {
			searchValue: '',
			lastSearch: null,
			searchTimeout: null,
			focused: false,
			focusedIndex: -1,

			// Checked in watcher.
			// Example of when this is needed:
			//		if user types customer number, then then before timeout that updates items
			//		and, there is only one item, this item should be selected
			filteredItemsWatcherShouldSelectIfLengthIsOneAndNotFocused: false,

			inputHasChangedAfterFocus: false,
			waitingForSearchTimeout: false,
		};
	},
	computed: {
		shouldShowPopover() {
			return this.popoverIsVisible
				&& this.filteredItems.length > 0
				&& (
					!this.showPopoverOnlyOnSearch
					|| this.searchValue.length > 0
				);
		},
		filteredItems() {

			if (this.searchValue === '') return this.items;

			return this.items.filter( item => {

				if (!item || !this.searchValue) {
					return false;
				}
				let searchString = this.searchValue.toLowerCase();

				if (this.itemFilterFunc)
					return this.itemFilterFunc(item, searchString);

				return this.filterItem(item, searchString);
			});
		},
		filterTitle() {
			return this.limit > 0
				&& this.limit <= this.filteredItems.length

				? `${ this.limit }+`
				: this.filteredItems.length;
		}
	},
	watch: {
		selectedItem: {

			immediate: true,
			handler(newVal) {

				if (newVal) {
					this.searchValue = this.getSearchValueFromSelectedItem(newVal, this.displayKey);

					if (this.eagerLoad)
						this.updateFilteredItems();
				}
			}
		},
		filteredItems: {
			immediate: true,
			handler(newVal) {
				// When you write quicker than the timeOut in eventuallyUpdateFilteredItems
				// and then you tab/enter out before the popover is shown, and there is only one option
				if (
					this.filteredItemsWatcherShouldSelectIfLengthIsOneAndNotFocused
					&& newVal.length === 1
					&& !this.focused
				) {
					this.$emit('select', newVal[0]);

					this.filteredItemsWatcherShouldSelectIfLengthIsOneAndNotFocused = false;

				} else if (
					this.filteredItemsWatcherShouldSelectIfLengthIsOneAndNotFocused
					&& !this.focused
				) {
					this.searchValue = this.getSearchValueFromSelectedItem(this.selectedItem, this.displayKey);
				}
			}
		}
	},
	mounted() {
		if (this.selectedItem) {

			this.searchValue = this.getSearchValueFromSelectedItem(this.selectedItem, this.displayKey);
		}
		if (this.eagerLoad) this.updateFilteredItems();
	},

	methods: {
		onInput(newSearchValue) {
			this.searchValue = newSearchValue;
			this.inputHasChangedAfterFocus = true;

			if (this.immediatelyUpdateFilteredItems) {
				this.updateFilteredItems();
				this.popoverSetVisible();

			} else
				this.eventuallyUpdateFilteredItems(true);

			this.$emit('input', newSearchValue);
		},
		eventuallyUpdateFilteredItems(shouldOpenPopover) {

			clearTimeout(this.searchTimeout);
			this.waitingForSearchTimeout = true;

			this.searchTimeout = setTimeout(() => {
				this.updateFilteredItems();

				this.waitingForSearchTimeout = false;

				// Optimally, the popover should show when items has updated, to prevent drawing popover with old items list.
				// However, the items list may be static. In such case we want to show it immediately.
				// TODO: See if this can be improved
				if (shouldOpenPopover && this.focused) this.popoverSetVisible();

			}, 250);
		},
		updateFilteredItems() {
			if (this.searchValue === '')
				return;

			if (
				this.items.length < this.limit
				&& this.searchValue.startsWith(this.lastSearch)
			) return;

			this.$emit('updateFilters', this.searchValue);
			this.lastSearch = this.searchValue;
		},

		filterItem(item, searchString) {
			if (!item)
				return false;

			if (this.displayKey)
				item = item[this.displayKey];

			let ret = true;

			for (let c of searchString.split(' ')) {

				ret = ret
					&& ( item
						&& item.toLowerCase().includes(c) );
			}
			return ret;
		},
		onButtonClick() {

			this.$emit('updateFilters', this.searchValue);
			this.popoverSetHidden();
			this.$emit('button:click', this.searchValue);

			this.focused = false;
		},
		onSelected(value) {

			this.popoverSetHidden();
			this.$emit('select', value);
			if (this.resetOnSelect) this.clearInput();

			this.focused = false;
		},
		onChange(value) {
			if (!value) this.$emit('select', {});
			this.$emit('change', value);
		},
		async onFocus(event) {
			event.target.select();
			await this.updateFilteredItems();

			this.focused = true;
			this.$emit('focus', event);
		},
		focus() {
			this.$refs.input.focus();
			this.focused = true;
		},
		blur() {
			this.$refs.input.blur();
			this.popoverSetHidden();
			this.focused = false;
		},
		clearInput() {
			this.searchValue = '';
			this.eventuallyUpdateFilteredItems(false);

			this.$emit('input', this.searchValue);
		},
		onBlur(event) {

			if (isNextFocusInMenuOptions(event, this.$refs.menuOptions)) {
				event.target.focus();

			} else {
				this.$emit('blur', event);
				this.popoverSetHidden();

				this.inputHasChangedAfterFocus = false;
				this.focused = false;

				if (
					!this.filteredItemsWatcherShouldSelectIfLengthIsOneAndNotFocused
					&& !isNextFocusInputButton(event, this.$refs.input)
				) {

					this.searchValue = this.getSearchValueFromSelectedItem(this.selectedItem, this.displayKey);
				}
			}
			function isNextFocusInMenuOptions(event, menuOptionsRef) {
				return event.relatedTarget
					&& menuOptionsRef
					&& menuOptionsRef.$el
					&& menuOptionsRef.$el.contains(event.relatedTarget);
			}
			function isNextFocusInputButton(event, inputButtonRef) {
				return event.relatedTarget
					&& inputButtonRef
					&& inputButtonRef.$el
					&& inputButtonRef.$el.contains(event.relatedTarget);
			}
		},
		onTab(event) {
			this.$emit('keyDownTab', event);

			this.onTabOrEnter();
		},
		onEnter(event) {
			this.$emit('keyDownEnter', event);

			this.onTabOrEnter();
		},
		onTabOrEnter() {
			if (this.waitingForSearchTimeout) {

				this.filteredItemsWatcherShouldSelectIfLengthIsOneAndNotFocused = true;

			} else if (
				!this.waitingForSearchTimeout
				&& this.inputHasChangedAfterFocus
			) {
				if (this.focusedIndex > -1 && this.filteredItems.length > 0) {
					// There is a bug where choosing a item from a list updates this.filteredItems
					// without updating this.focusedIndex causing the emmitance of undefined values.
					// But since this component is being phased out I haven't tried fixing it.
					this.$emit('select', this.filteredItems[this.focusedIndex]);
				}
			}
		},
		getSearchValueFromSelectedItem(selectedItem, displayKey) {

			if (selectedItem)
				return ( displayKey
					? selectedItem[displayKey]
					: selectedItem )
					|| '';

			else return '';
		},
	},
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
@import '~@/components/evergreen/scss/text';

.item {
	display: flex;
	flex-direction: column;
	justify-content: center;
	height: 100%;
}

.item-main-text {
	height:16px;
	line-height: 16px;
	font-weight: 600;
	overflow: hidden;
	white-space: nowrap;
}

.item-sub-text {
	height:16px;
	font-size: 10px;
	font-weight: normal;
	font-style: normal;
	font-stretch: normal;
	line-height: 1.6;
	letter-spacing: normal;
	overflow: hidden;
	white-space: nowrap;
}

.details {
	@extend %text-small;
	color: get-color($text, 'muted');
	padding-left: 10px;
}

.first-line {
	display: grid;
	column-gap: 20px;
}
</style>
